"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createStylesheet = exports.createTwoColumnPrintStylesheet = exports.createScreenStylesheet = exports.createPrintStylesheet = void 0;
const jss_1 = __importDefault(require("jss"));
const jss_plugin_global_1 = __importDefault(require("jss-plugin-global"));
const jss_preset_default_1 = __importDefault(require("jss-preset-default"));
const types_1 = require("./types");
const screenStyles_1 = require("./screenStyles");
const computedHorizontals_1 = require("./computedHorizontals");
const makeMarginStyles_1 = require("./makeMarginStyles");
// default preset gives us camelCase -> kebab-case:  marginTop -> margin-top
jss_1.default.setup((0, jss_preset_default_1.default)());
// global plugin gives us unaltered style names instead of uniqified ones
jss_1.default.use((0, jss_plugin_global_1.default)());
// these are classes that are added to dom elements
const blockSelectors = {
    general: '.o-general',
    new_act: '.o-new-act',
    end_of_act: '.o-end-of-act',
    action: '.o-action',
    sceneHeading: '.o-scene',
    transition: '.o-transition',
    slug: '.o-slug',
    bracket: '.o-bracket',
    dual_dialogue: '.o-dual-dialogue',
    dual_dialogue_column: '.o-dual-dialogue__column',
    dialogue: '.o-dialogue',
    character: '.o-character',
    parenthetical: '.o-parenthetical',
};
const getSelector = ({ blockType, isDual, }) => {
    const classNames = [];
    if (isDual) {
        classNames.push('.o-dual-dialogue');
    }
    classNames.push(blockSelectors[blockType]);
    return classNames.join(' ');
};
// get the css selector for element number pseudo-elements.
// example:  .o-dialogue[data-element-number]::before
const getElementNumberSelector = ({ blockType, }) => {
    const baseSelector = blockSelectors[blockType];
    return `.is-static ${baseSelector}[data-element-number]::before`;
};
const toWidth = (charValue) => `${(0, computedHorizontals_1.chToPx)(charValue)}px`;
const makeBlockStyle = (config, options) => {
    const { blockTopMargin, marginLeft, width, color, lineHeight, underline, uppercase, italic, bold, alignment, } = config;
    const colorToUse = options.monochrome ? '#000' : color;
    return {
        marginTop: `${blockTopMargin}em`,
        marginLeft: toWidth(marginLeft),
        width: toWidth(width),
        lineHeight: `${lineHeight}em`,
        // use !important on color to override inline styling in prosemirror toDOM
        color: colorToUse ? `${colorToUse} !important` : undefined,
        textAlign: alignment,
        textDecoration: underline ? 'underline' : undefined,
        textTransform: uppercase ? 'uppercase' : undefined,
        fontStyle: italic ? 'italic' : undefined,
        fontWeight: bold ? 'bold' : undefined,
    };
};
const makeTwoColumnBlockStyle = (config, options) => {
    const { color, underline, uppercase, italic, bold } = config;
    const colorToUse = options.monochrome ? '#000' : color;
    const shouldUnderline = underline && !options.suppressUnderline;
    return {
        color: colorToUse,
        textDecoration: shouldUnderline ? 'underline' : undefined,
        textTransform: uppercase ? 'uppercase' : undefined,
        fontStyle: italic ? 'italic' : undefined,
        fontWeight: bold ? 'bold' : undefined,
    };
};
const buildTwoColumnPrintStyles = (configuration, options) => {
    const styles = {};
    types_1.ALL_BLOCK_TYPES.forEach((blockType) => {
        const selector = getSelector({ blockType, isDual: false });
        styles[selector] = makeTwoColumnBlockStyle(configuration[blockType], {
            suppressUnderline: blockType === 'slug',
            monochrome: options.monochrome,
        });
    });
    return styles;
};
const mergeStyles = (styleSets) => {
    const result = {};
    styleSets.forEach((set) => Object.assign(result, set));
    return result;
};
const buildBaseStyles = (configuration, options) => {
    const styles = {};
    const { monochrome } = options;
    // for the uniform block types, build the simple css
    // like for .o-slug
    types_1.UNIFORM_BLOCK_TYPES.forEach((blockType) => {
        // selector is like .o-slug or o.dialogue
        const selector = getSelector({ blockType, isDual: false });
        const config = configuration[blockType];
        styles[selector] = makeBlockStyle(config, { monochrome });
    });
    // for the dual format block types, build both the top
    // level style (e.g. .o-dialogue) as well as the dual-format
    // style (e.g. .o-dual-dialogue .o-dialogue)
    types_1.DUAL_FORMAT_BLOCK_TYPES.forEach((blockType) => {
        const mainSelector = getSelector({ blockType, isDual: false });
        const dualSelector = getSelector({ blockType, isDual: true });
        const { dualDialogOverrides, ...baseConfig } = configuration[blockType];
        styles[mainSelector] = makeBlockStyle(baseConfig, { monochrome });
        styles[dualSelector] = makeBlockStyle(dualDialogOverrides, { monochrome });
    });
    // add computed stylings for element-number pseudo selectors. We do this for all block
    // types even though not all of them are numberable (the styles just are never applied)
    types_1.ALL_BLOCK_TYPES.forEach((blockType) => {
        const config = configuration[blockType];
        const selector = getElementNumberSelector({ blockType });
        styles[selector] = { left: `${(0, computedHorizontals_1.elementNumberLeftPx)(config.marginLeft)}px` };
    });
    return styles;
};
const buildScreenStyles = (configuration, options) => ({
    ...buildBaseStyles(configuration, options),
    ...(0, screenStyles_1.configureScreenStyles)(configuration),
});
const createPrintStylesheet = (configuration, options) => {
    return jss_1.default.createStyleSheet({
        '@global': buildBaseStyles(configuration, options ?? { monochrome: false }),
    });
};
exports.createPrintStylesheet = createPrintStylesheet;
const createScreenStylesheet = (configuration, options) => {
    return jss_1.default.createStyleSheet({
        '@global': buildScreenStyles(configuration, options ?? { monochrome: false }),
    });
};
exports.createScreenStylesheet = createScreenStylesheet;
const createTwoColumnPrintStylesheet = (configuration, options) => {
    return jss_1.default.createStyleSheet({
        '@global': buildTwoColumnPrintStyles(configuration, options ?? { monochrome: false }),
    });
};
exports.createTwoColumnPrintStylesheet = createTwoColumnPrintStylesheet;
// The exported print stylesheets are only for the server-side pdf exports. This
// should be used for all screen styles and for the browser-based printing.
// (These systems will eventually merge)
const createStylesheet = (config, options) => {
    const { media, vars = {} } = options;
    const styleSets = [
        buildBaseStyles(config.blocks, { monochrome: !!options.monochrome }),
    ];
    if (media === 'screen') {
        styleSets.push(buildScreenStyles(config.blocks, { monochrome: !!options.monochrome }));
    }
    styleSets.push((0, makeMarginStyles_1.makeMarginStyles)(config.margins, vars));
    return jss_1.default.createStyleSheet({ '@global': mergeStyles(styleSets) });
};
exports.createStylesheet = createStylesheet;
